import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Direction from './Direction';
import { DirectionData } from '../../types/PersonalData';
import { SessionChecker } from '../../utils/SessionChecker';
import { BackMappings } from '../../utils/BackMappings';
import { useAppStore } from '../../store/AppContext';
import { useAuthStore } from '../../store/AuthContext';
import { HostProvider } from '../../utils/HostProvider';
import { StorageHelper } from '../../utils/StorageHelper';
import { MisDatosHelper } from './MisDatosHelper';

interface Props {
  tipo: string;
  setTipo: (tipo: string) => void;
  directionData: DirectionData[];
  setDirectionData: (directionData: DirectionData[]) => void;
  cargarDirecciones: () => void;
  setOnLoad: (onLoad: boolean) => void;
  cuentaBloqueada: boolean;
}

const Directions: FC<Props> = (props) => {
  const {
    cargarDirecciones,
    tipo,
    setTipo,
    directionData,
    setOnLoad,
    cuentaBloqueada,
  } = props;
  const { t } = useTranslation();
  const [dirHabitual, setDirHabitual] = useState<DirectionData[]>([]);
  const [dirEntrega, setDirEntrega] = useState<DirectionData[]>([]);
  const [dirFacturacion, setDirFacturacion] = useState<DirectionData[]>([]);
  const dataApp = useAppStore();
  const dataUser = useAuthStore();
  const isEroski = HostProvider.isEroski();
  const isCaprabo = HostProvider.isCaprabo();

  const titleDirectionsBlock = isEroski
    ? t('datos.directions.title')
    : t('caprabo.directions.title');

  const showForm = (type: string) => {
    if (type === 'entrega') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      document.getElementById('new-entrega').classList.add('visible');
    } else if (type === 'facturación') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      document.getElementById('new-facturacion').classList.add('visible');
    }
  };
  function mostrarFormDireccionFacturacion() {
    setTipo('facturación');
    showForm('facturación');
    document.body.classList.add('fix');
    setOnLoad(true);
  }

  useEffect(() => {
    if (directionData != null) {
      setDirHabitual(directionData.filter((d) => d.tipoDireccion === 0));
      setDirEntrega(directionData.filter((d) => d.tipoDireccion === 1));
      setDirFacturacion(directionData.filter((d) => d.tipoDireccion === 2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directionData]);

  const clicAniadirDireccionEntrega = async () => {
    const responseCheckSession = await SessionChecker.checkSession(
      BackMappings.MISDATOS,
      dataApp.lang,
      dataUser.user.isApp === 'true',
      dataUser.user.isRecuerdame,
    );
    if (!responseCheckSession) {
      return;
    }
    setTipo('entrega');
    showForm('entrega');
    document.body.classList.add('fix');
    const labelError = document.getElementById(
      `codPostal-error-not-selected-`,
    ) as HTMLLabelElement;
    if (labelError !== null) {
      labelError.classList.add('hide');
    }
    const componenteCp = document.getElementById(
      `cod-entrega-`,
    ) as HTMLLabelElement;
    if (componenteCp !== null) {
      componenteCp.classList.remove('error');
    }
    setOnLoad(true);
  };

  useEffect(() => {
    const fromApp = StorageHelper.getFromApp() as string;
    if (fromApp === 'web_ecommerce' || fromApp === 'app_mcommerce') {
      const nuevaDireccionEntrega = StorageHelper.getNuevaDireccionEntrega() as string;
      if (
        nuevaDireccionEntrega !== null &&
        nuevaDireccionEntrega !== undefined &&
        nuevaDireccionEntrega === '1'
      ) {
        StorageHelper.removeNuevaDireccionEntrega();
        clicAniadirDireccionEntrega();
      }
    }
    const padre = document.getElementById(
      `bloque-datos__direccion-facturacion`,
    ) as HTMLDivElement;
    const scrolDireccionFacturacion = StorageHelper.getScrollFacturacion() as string;
    const scrollYAbrirDirFacturacion = StorageHelper.getScrollAbrirFacturacion() as string;
    if (padre !== null) {
      const misDatosHelper = new MisDatosHelper();
      if (
        Boolean(scrollYAbrirDirFacturacion) &&
        scrollYAbrirDirFacturacion.trim().toLowerCase() === 'true'
      ) {
        misDatosHelper.scrollToResponsive(padre);
        mostrarFormDireccionFacturacion();
        StorageHelper.removeScrollAbrirFacturacion();
      } else if (
        scrolDireccionFacturacion !== null &&
        scrolDireccionFacturacion !== undefined
      ) {
        misDatosHelper.scrollToResponsive(padre);
        StorageHelper.removeScrollFacturacion();
      }
    }
  });

  return (
    <>
      <div className="divider" />

      <div className="bloque-datos">
        <h2 className="bloque-datos__title u-text-brand">
          <span className="divider__square brand-color" />
          <span className="u-margin-right">{titleDirectionsBlock}</span>
        </h2>
        {!cuentaBloqueada && (
          <div className="bloque-datos__direccion">
            <h3
              id="habitual"
              className="bloque-datos__subtitle u-margin-bottom-small"
            >
              {t('datos.directions.habitual')}
            </h3>

            <div className="bloque-direccion_layout">
              {dirHabitual.map((e) => (
                <Direction
                  key={e.idDireccion}
                  name={e.directionName}
                  street={e.roadName}
                  number={e.number}
                  city={e.locality}
                  removable
                  directionData={e}
                  tipoForm="habitual"
                  tipo={tipo}
                  setTipo={setTipo}
                  cargarDirecciones={cargarDirecciones}
                  setOnLoad={setOnLoad}
                />
              ))}
            </div>
          </div>
        )}

        {(() => {
          if (isEroski || isCaprabo) {
            return (
              <>
                {!cuentaBloqueada && <div className="divider__small" />}
                <div className="bloque-datos__direccion">
                  <h3
                    id="entrega"
                    className="bloque-datos__subtitle u-margin-bottom-small"
                  >
                    {t('datos.directions.delivery')}
                  </h3>

                  <div className="bloque-direccion_layout">
                    {dirEntrega.map((e) => (
                      <Direction
                        key={e.idDireccion}
                        name={e.directionName}
                        street={e.roadName}
                        number={e.number}
                        city={e.locality}
                        directionData={e}
                        tipoForm="entrega"
                        tipo={tipo}
                        setTipo={setTipo}
                        cargarDirecciones={cargarDirecciones}
                        setOnLoad={setOnLoad}
                      />
                    ))}
                  </div>

                  <p className="bloque-datos__link">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <a className="link" onClick={clicAniadirDireccionEntrega}>
                      + {t('datos.directions.addDirDelivery')}
                    </a>
                  </p>
                </div>
                <div className="divider__small" />
                <div
                  className="bloque-datos__direccion"
                  id="bloque-datos__direccion-facturacion"
                >
                  <h3
                    id="facturación"
                    className="bloque-datos__subtitle u-margin-bottom-small"
                  >
                    {t('datos.directions.billing')}
                  </h3>

                  <div className="bloque-direccion_layout">
                    {dirFacturacion.map((e) => (
                      <Direction
                        key={e.idDireccion}
                        name={e.directionName}
                        street={e.roadName}
                        number={e.number}
                        city={e.locality}
                        directionData={e}
                        tipoForm="facturación"
                        tipo={tipo}
                        setTipo={setTipo}
                        cargarDirecciones={cargarDirecciones}
                        setOnLoad={setOnLoad}
                      />
                    ))}
                  </div>

                  <p className="bloque-datos__link">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <a
                      className="link"
                      onClick={async () => {
                        const responseCheckSession = await SessionChecker.checkSession(
                          BackMappings.MISDATOS,
                          dataApp.lang,
                          dataUser.user.isApp === 'true',
                          dataUser.user.isRecuerdame,
                        );
                        if (!responseCheckSession) {
                          return;
                        }
                        mostrarFormDireccionFacturacion();
                      }}
                    >
                      + {t('datos.directions.addDirBilling')}
                    </a>
                  </p>
                </div>
              </>
            );
          }
          return <></>;
        })()}
      </div>
    </>
  );
};

export default Directions;
